/**
 * OffPlatformView
 *
 * Shows volume Beu is NOT processing: cash, non-Beu card readers, "other".
 * This is the opportunity metric — money we'd make if these txns flowed through Beu.
 *
 * Plus a per-salon league table of biggest off-platform leakers.
 */

import React, { Component } from 'react';
import { adminService } from '../../../_services/admin.service';

const fmtGBP = (n) => (n == null || isNaN(n)) ? '—' : '£' + Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (n) => (n == null || isNaN(n)) ? '—' : Number(n).toLocaleString('en-GB');
const pct = (n) => (n == null || isNaN(n)) ? '—' : `${(Number(n) * 100).toFixed(1)}%`;

class OffPlatformView extends Component {
    state = { loading: true, error: null, data: null, sortKey: 'missedVolume', sortDir: 'desc' };

    componentDidMount() { this.fetch(); }
    componentDidUpdate(prevProps) {
        if (prevProps.startDate !== this.props.startDate || prevProps.endDate !== this.props.endDate) {
            this.fetch();
        }
    }

    fetch = async () => {
        this.setState({ loading: true, error: null });
        try {
            const { startDate, endDate } = this.props;
            const res = await adminService.getFinancialsOffPlatform({ startDate, endDate });
            if (res.statusCode === 1) {
                this.setState({ data: res.responseData, loading: false });
            } else {
                this.setState({ error: res.message || 'Failed to load off-platform data', loading: false });
            }
        } catch (err) {
            console.error('financials off-platform error', err);
            this.setState({ error: String(err), loading: false });
        }
    };

    setSort = (key) => {
        this.setState(s => ({
            sortKey: key,
            sortDir: s.sortKey === key && s.sortDir === 'desc' ? 'asc' : 'desc'
        }));
    };

    renderSummary(summary) {
        if (!summary) return null;
        const onPlatformPct = summary.onPlatformVolume + summary.offPlatformVolume > 0
            ? summary.onPlatformVolume / (summary.onPlatformVolume + summary.offPlatformVolume)
            : 0;
        const tiles = [
            { label: 'Off-platform volume', value: fmtGBP(summary.offPlatformVolume), sub: `${fmtInt(summary.offPlatformTxns)} transactions` },
            { label: 'Missed Beu revenue', value: fmtGBP(summary.missedRevenue), sub: '1.25% + 20p if routed through Beu' },
            { label: 'On-platform volume', value: fmtGBP(summary.onPlatformVolume), sub: `${fmtInt(summary.onPlatformTxns)} transactions` },
            { label: 'On-platform %', value: pct(onPlatformPct), sub: 'Target: rising month on month' }
        ];
        return (
            <div className="fin-kpi-strip">
                {tiles.map(t => (
                    <div key={t.label} className="fin-kpi-tile">
                        <div className="fin-kpi-tile-label">{t.label}</div>
                        <div className="fin-kpi-tile-value">{t.value}</div>
                        {t.sub && <div className="fin-kpi-tile-sub">{t.sub}</div>}
                    </div>
                ))}
            </div>
        );
    }

    renderByType(byType) {
        if (!byType?.length) return null;
        return (
            <div className="fin-section">
                <div className="fin-section-header">
                    <h3 className="fin-section-title">By payment method</h3>
                    <span className="fin-section-sub">Current month</span>
                </div>
                <table className="fin-table">
                    <thead>
                        <tr>
                            <th>Method</th>
                            <th className="num">Volume</th>
                            <th className="num">Txns</th>
                            <th className="num">Avg txn</th>
                            <th className="num">Missed Beu rev</th>
                        </tr>
                    </thead>
                    <tbody>
                        {byType.map(r => (
                            <tr key={r.paymentType}>
                                <td>{r.label}</td>
                                <td className="num">{fmtGBP(r.volume)}</td>
                                <td className="num">{fmtInt(r.txns)}</td>
                                <td className="num">{fmtGBP(r.txns ? r.volume / r.txns : 0)}</td>
                                <td className="num">{fmtGBP(r.missedRevenue)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    renderSalonLeague(salons) {
        if (!salons?.length) return null;
        const { sortKey, sortDir } = this.state;
        const sorted = [...salons].sort((a, b) => {
            const av = a[sortKey] ?? 0;
            const bv = b[sortKey] ?? 0;
            return sortDir === 'desc' ? bv - av : av - bv;
        });

        const header = (key, label, cls = '') => (
            <th
                className={cls}
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => this.setSort(key)}
            >
                {label}
                {sortKey === key && <span style={{ marginLeft: 4 }}>{sortDir === 'desc' ? '↓' : '↑'}</span>}
            </th>
        );

        return (
            <div className="fin-section">
                <div className="fin-section-header">
                    <h3 className="fin-section-title">Top leakers — per salon</h3>
                    <span className="fin-section-sub">Click column headers to sort · top 50 shown</span>
                </div>
                <table className="fin-table">
                    <thead>
                        <tr>
                            {header('salonName', 'Salon')}
                            {header('totalVolume', 'Total volume', 'num')}
                            {header('offPlatformVolume', 'Off-platform £', 'num')}
                            {header('offPlatformPct', 'Off %', 'num')}
                            {header('missedVolume', 'Missed Beu rev', 'num')}
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.slice(0, 50).map(s => (
                            <tr key={s.salonId}>
                                <td>{s.salonName || '—'}</td>
                                <td className="num">{fmtGBP(s.totalVolume)}</td>
                                <td className="num">{fmtGBP(s.offPlatformVolume)}</td>
                                <td className="num">{pct(s.offPlatformPct)}</td>
                                <td className="num">{fmtGBP(s.missedVolume)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    renderZeroBeuSalons(salons) {
        if (!salons?.length) return null;
        const totalMissed = salons.reduce((s, r) => s + (r.missedBeuRevenue || 0), 0);
        return (
            <div className="fin-section">
                <div className="fin-section-header">
                    <h3 className="fin-section-title">
                        Conversion hit-list — salons taking bookings but 0% Beu payments
                    </h3>
                    <span className="fin-section-sub">
                        {salons.length} salon{salons.length === 1 ? '' : 's'} · {fmtGBP(totalMissed)} missed / month
                    </span>
                </div>
                <p className="fin-dim" style={{ fontSize: 12, marginTop: -8, marginBottom: 12 }}>
                    These salons are booking through Beu but taking every payment off-platform (cash, external card, other).
                    Pick up the phone — direct conversion opportunity.
                </p>
                <table className="fin-table">
                    <thead>
                        <tr>
                            <th>Salon</th>
                            <th className="num">Off-platform £ MTD</th>
                            <th className="num">Txns</th>
                            <th className="num">Missed Beu rev</th>
                        </tr>
                    </thead>
                    <tbody>
                        {salons.map(s => (
                            <tr key={s.salonId}>
                                <td>{s.salonName || '—'}</td>
                                <td className="num">{fmtGBP(s.offPlatformVolume)}</td>
                                <td className="num">{fmtInt(s.offPlatformTxns)}</td>
                                <td className="num">{fmtGBP(s.missedBeuRevenue)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    render() {
        const { loading, error, data } = this.state;
        if (loading) return <div className="fin-loading">Loading off-platform data…</div>;
        if (error) return <div className="fin-error">{error}</div>;
        if (!data) return null;

        return (
            <div>
                {this.renderSummary(data.summary)}
                {this.renderZeroBeuSalons(data.zeroBeuSalons)}
                {this.renderByType(data.byType)}
                {this.renderSalonLeague(data.bySalon)}
            </div>
        );
    }
}

export default OffPlatformView;
